import React from 'react';

// Avani-branded inline loader. Replaces the old grey/blue spinner everywhere
// (ProtectedRoute, dashboards, page-level fetches) — every consumer just
// imports Spinner so this rewrite propagates automatically.
//
// Three layered animations sell the "magical" feel without adding deps:
//  1. A brown gradient ring that rotates around the badge
//  2. The "A" badge gently pulses
//  3. AVANI letters shimmer with a sweeping highlight
//
// Sizes:
//   <Spinner />           — default (medium, ~88px)
//   <Spinner size="sm" /> — compact for inline / button use (~48px)
//   <Spinner size="lg" /> — hero-card loaders (~128px)
const SIZES = {
    xs: { ring: 20,  letter: 'text-[9px]', word: 'text-[8px]',  gap: 'mt-1' },
    sm: { ring: 48,  letter: 'text-base',  word: 'text-[10px]', gap: 'mt-2' },
    md: { ring: 88,  letter: 'text-2xl',   word: 'text-xs',     gap: 'mt-3' },
    lg: { ring: 128, letter: 'text-4xl',   word: 'text-sm',     gap: 'mt-4' }
};

const Spinner = ({ size = 'md', label = 'Loading' }) => {
    const s = SIZES[size] || SIZES.md;
    const innerPad = Math.round(s.ring * 0.12);

    return (
        <div className="inline-flex flex-col items-center justify-center" role="status" aria-label={label}>
            {/* Halo + rotating ring + A badge */}
            <div
                className="relative flex items-center justify-center"
                style={{ width: s.ring, height: s.ring }}
            >
                {/* Soft amber halo behind the ring */}
                <div className="absolute inset-0 rounded-full bg-amber-300/30 blur-xl animate-soft-pulse" />

                {/* Rotating conic-gradient ring */}
                <div
                    className="absolute inset-0 rounded-full animate-avani-spin"
                    style={{
                        background: 'conic-gradient(from 0deg, transparent 0deg, #8a6144 90deg, #f0c897 180deg, #8a6144 270deg, transparent 360deg)',
                        WebkitMask: `radial-gradient(circle, transparent ${s.ring / 2 - 6}px, black ${s.ring / 2 - 5}px)`,
                        mask: `radial-gradient(circle, transparent ${s.ring / 2 - 6}px, black ${s.ring / 2 - 5}px)`
                    }}
                />

                {/* A badge — pulses in place */}
                <div
                    className="relative rounded-full flex items-center justify-center font-extrabold animate-avani-pulse shadow-lg"
                    style={{
                        width: s.ring - innerPad * 2,
                        height: s.ring - innerPad * 2,
                        background: 'radial-gradient(circle at 30% 30%, #fff5e6 0%, #f0c897 60%, #c69877 100%)',
                        color: '#5d4332'
                    }}
                >
                    <span
                        className={`${s.letter} bg-clip-text text-transparent`}
                        style={{
                            backgroundImage: 'linear-gradient(135deg, #5d4332 0%, #8a6144 50%, #5d4332 100%)'
                        }}
                    >
                        A
                    </span>
                </div>
            </div>

            {/* AVANI word + dot ticker (hidden on xs/sm to keep it compact) */}
            {size !== 'sm' && size !== 'xs' && (
                <div className={`${s.gap} flex items-center gap-2`}>
                    <span
                        className={`${s.word} font-extrabold tracking-[0.4em] uppercase bg-clip-text text-transparent bg-[length:200%_auto] animate-shimmer`}
                        style={{
                            backgroundImage: 'linear-gradient(90deg, #8a6144 0%, #f0c897 50%, #8a6144 100%)'
                        }}
                    >
                        AVANI
                    </span>
                    <span className="flex items-center gap-0.5">
                        {[0, 1, 2].map((i) => (
                            <span
                                key={i}
                                className="inline-block w-1 h-1 rounded-full bg-[#8a6144] animate-avani-dot"
                                style={{ animationDelay: `${i * 0.18}s` }}
                            />
                        ))}
                    </span>
                </div>
            )}

            {/* Local keyframes — kept in-component so the spinner is fully
                drop-in: nothing to add to tailwind.config.js or index.css. */}
            <style>{`
                @keyframes avani-spin { to { transform: rotate(360deg); } }
                .animate-avani-spin { animation: avani-spin 1.6s linear infinite; }

                @keyframes avani-pulse {
                    0%, 100% { transform: scale(1); }
                    50%      { transform: scale(0.92); }
                }
                .animate-avani-pulse { animation: avani-pulse 1.6s ease-in-out infinite; }

                @keyframes avani-dot {
                    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
                    40%           { opacity: 1;    transform: translateY(-2px); }
                }
                .animate-avani-dot { animation: avani-dot 1.2s ease-in-out infinite; }
            `}</style>
        </div>
    );
};

export default Spinner;
